import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  Text,
  Alert,
  Loader,
  Badge,
  Table,
  Divider,
  DetailField,
  ProductImageAvatar,
  formatAddress,
} from '@vipso/shared';
import { useGetProcurementQuery } from '../api/tenantApi';
import { useDate } from '../hooks/useDate';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import {
  openArgsFromFileUrl,
  useFileViewer,
} from '../features/files/FileViewerContext';

function formatCurrency(value?: number | null) {
  if (value == null) return null;
  return `₹${Number(value).toFixed(2)}`;
}

function prettyPct(pct: number) {
  if (!Number.isFinite(pct)) return '0';
  if (Number.isInteger(pct)) return String(pct);
  return pct.toFixed(2).replace(/\.?0+$/, '');
}

/** Prefer stored PERCENT rate; else infer a clean % from amount/base for older rows. */
function formatRateLabel(
  base: string,
  opts: {
    type?: string | null;
    rate?: number | null;
    amount?: number | null;
    baseAmount?: number | null;
    flatSuffix: string;
  },
) {
  const type = String(opts.type ?? '').toUpperCase();
  if (type === 'PERCENT' && opts.rate != null && Number(opts.rate) > 0) {
    return `${base} (${prettyPct(Number(opts.rate))}%)`;
  }
  if (type === 'FLAT') {
    return `${base} ${opts.flatSuffix}`.trim();
  }
  const amount = Number(opts.amount ?? 0);
  const baseAmount = Number(opts.baseAmount ?? 0);
  if (!type && amount > 0 && baseAmount > 0) {
    const pct = (amount / baseAmount) * 100;
    const rounded = Math.round(pct);
    if (Math.abs(pct - rounded) < 0.05 && rounded > 0 && rounded <= 100) {
      return `${base} (${rounded}%)`;
    }
  }
  return base;
}

export function ProcurementDetailPage() {
  const { t } = useTranslation();
  const { openFileViewer } = useFileViewer();
  const customerTerms = useModuleTerminology('customer');
  const vendorTerms = useModuleTerminology('vendor');
  const productTerms = useModuleTerminology('product');
  const approvalAccess = useApprovalModuleAccess();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { format } = useDate();
  const { data, isLoading, isError } = useGetProcurementQuery(id!, { skip: !id });

  const proc = data?.getProcurement;
  const approvalEnabled =
    proc?.procType === 'SALES'
      ? approvalAccess.procSales
      : proc?.procType === 'PURCHASE'
        ? approvalAccess.procPurchase
        : false;
  const party = proc?.customer ?? proc?.vendor;
  const partyAddress = formatAddress(proc?.customer?.address) || formatAddress(proc?.vendor?.address);
  const empty = t('common.emptyValue');

  function handleDownloadInvoice() {
    const url = proc?.procUrl?.trim();
    if (!url) {
      window.alert(t('orders.invoiceUnavailable'));
      return;
    }
    const args = openArgsFromFileUrl(url, `order-${proc?.id ?? id}.pdf`);
    if (!args) {
      window.alert(t('orders.invoiceUnavailable'));
      return;
    }
    openFileViewer(args);
  }

  if (isLoading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isError || !proc) {
    return (
      <Alert color="red" title={t('orders.notFound')}>
        <Button variant="light" mt="sm" onClick={() => navigate('/orders')}>
          {t('orders.backToOrders')}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('orders.detailTitle', { party: party?.name || t('orders.title') })}
        description={
          proc.ordNumber
            ? t('orders.orderNumber', { number: proc.ordNumber })
            : t('orders.orderDetails')
        }
        actions={
          <Group>
            <Button variant="default" onClick={() => navigate('/orders')}>
              {t('common.back')}
            </Button>
            <Button onClick={handleDownloadInvoice}>{t('orders.downloadInvoice')}</Button>
          </Group>
        }
      />

      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <Group gap="sm">
            <Badge color={proc.procType === 'SALES' ? 'green' : 'blue'}>
              {proc.procType === 'SALES' ? t('orders.sales') : t('orders.purchase')}
            </Badge>
            {approvalEnabled ? (
              <ApprovalStatusBadge status={proc.approvalStatus} />
            ) : null}
            <Text size="sm" c="dimmed">
              {proc.procType === 'SALES'
                ? t('orders.fromCustomer', { entity: customerTerms.singular.toLowerCase() })
                : t('orders.fromVendor', { entity: vendorTerms.singular.toLowerCase() })}
            </Text>
          </Group>
          <DetailField
            label={t('orders.orderNumberLabel')}
            value={proc.ordNumber ? `#${proc.ordNumber}` : undefined}
          />
          <DetailField label={t('common.createdOn')} value={format(proc.createdAt, 'date')} />
          <DetailField
            label={proc.customer ? customerTerms.singular : vendorTerms.singular}
            value={party?.name}
          />
          <DetailField label={t('common.phone')} value={party?.phone} />
          <DetailField label={t('common.address')} value={partyAddress} />
          <DetailField
            label={t('orders.labour')}
            value={
              proc.labour
                ? `${proc.labour.name}${proc.labour.phone ? ` (${proc.labour.phone})` : ''}`
                : undefined
            }
          />
          <DetailField
            label={t('orders.transport')}
            value={
              proc.transport
                ? `${proc.transport.name}${proc.transport.phone ? ` (${proc.transport.phone})` : ''}`
                : undefined
            }
          />
          <DetailField label={t('orders.vehicleNumber')} value={proc.vehicleNo} />
          <DetailField label={t('orders.biltyNumber')} value={proc.biltyNo} />
          <DetailField label={t('orders.receiptNumber')} value={proc.receiptNumber} />
          <DetailField label={t('orders.gatePassNumber')} value={proc.gatePassNumber} />
          <DetailField label={t('orders.loadNumber')} value={proc.loadNumber} />
          <DetailField label={t('orders.bagNumber')} value={proc.bagNumber} />
          {approvalEnabled ? (
            <>
              <DetailField
                label={t('orders.approvedBy')}
                value={
                  proc.approvedBy && typeof proc.approvedBy === 'object' && 'name' in proc.approvedBy
                    ? String((proc.approvedBy as { name?: string }).name ?? '')
                    : undefined
                }
              />
              <DetailField
                label={t('orders.approvedAt')}
                value={proc.approvedAt ? format(proc.approvedAt, 'date') : undefined}
              />
            </>
          ) : null}
          <DetailField label={t('common.remarks')} value={proc.remarks} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="PROC"
        subjectId={proc.id}
        entityApprovalStatus={proc.approvalStatus}
        enabled={approvalEnabled}
      />

      <Paper p="lg" withBorder radius="md">
        <Text fw={600} mb="md">
          {t('common.products')}
        </Text>
        <Table.ScrollContainer minWidth={500}>
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('orders.colProduct', { entity: productTerms.singular })}</Table.Th>
                <Table.Th>{t('orders.colLocation')}</Table.Th>
                <Table.Th>{t('orders.colQty')}</Table.Th>
                <Table.Th>{t('orders.colLoss')}</Table.Th>
                <Table.Th>{t('orders.colPrice')}</Table.Th>
                <Table.Th>{t('orders.colSubtotal')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(proc.products ?? []).map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ProductImageAvatar
                        src={line.product?.imageUrl}
                        name={line.product?.name || '?'}
                        size={36}
                        onClick={
                          line.product?.imageFileId || line.product?.imageUrl
                            ? () => {
                                if (line.product?.imageFileId) {
                                  openFileViewer({ fileId: line.product.imageFileId });
                                } else if (line.product?.imageUrl) {
                                  openFileViewer({ url: line.product.imageUrl });
                                }
                              }
                            : undefined
                        }
                      />
                      <Stack gap={2}>
                        <Text size="sm">{line.product?.name || empty}</Text>
                        {line.addOnItemQty ? (
                          <Text size="xs" c="dimmed">
                            {t('orders.addOn', {
                              qty: line.addOnItemQty,
                              name: line.product?.addOnItemName ?? '',
                            })}
                          </Text>
                        ) : null}
                      </Stack>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {[line.chamber?.name, line.floor?.name, line.rack?.name]
                        .filter(Boolean)
                        .join(' / ') || empty}
                    </Text>
                  </Table.Td>
                  <Table.Td>{line.quantity ?? empty}</Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      {Number(line.lossPercent || 0)}% / {Number(line.lossKg || 0)} kg
                    </Text>
                  </Table.Td>
                  <Table.Td>{formatCurrency(line.pricePerUnit) ?? empty}</Table.Td>
                  <Table.Td>{formatCurrency(line.productPrice) ?? empty}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Paper p="lg" withBorder radius="md">
        <Stack gap="sm">
          {(() => {
            const productsSubtotal = (proc.products ?? []).reduce(
              (sum, line) => sum + Number(line.productPrice ?? 0),
              0,
            );
            const chargesDir = String(proc.chargesDirection ?? 'add').toLowerCase();
            const chargesLabel = proc.chargesType?.trim() || t('orders.charges');
            return (
              <>
                {productsSubtotal > 0 ? (
                  <Group justify="space-between">
                    <Text size="sm">{t('orders.productsSubtotal')}</Text>
                    <Text size="sm" fw={600}>
                      {formatCurrency(productsSubtotal)}
                    </Text>
                  </Group>
                ) : null}
                {(proc.discountAmount ?? 0) > 0 ? (
                  <Group justify="space-between">
                    <Text size="sm">
                      {formatRateLabel(t('orders.discount'), {
                        type: proc.discountType,
                        rate: proc.discount,
                        amount: proc.discountAmount,
                        baseAmount: productsSubtotal,
                        flatSuffix: t('orders.flatSuffix'),
                      })}
                    </Text>
                    <Text size="sm" fw={600} c="red">
                      − {formatCurrency(proc.discountAmount)}
                    </Text>
                  </Group>
                ) : null}
                {(proc.taxAmount ?? 0) > 0 ? (
                  <Group justify="space-between">
                    <Text size="sm">
                      {formatRateLabel(t('orders.tax'), {
                        type: proc.taxType,
                        rate: proc.tax,
                        amount: proc.taxAmount,
                        baseAmount: productsSubtotal,
                        flatSuffix: t('orders.flatSuffix'),
                      })}
                    </Text>
                    <Text size="sm" fw={600}>
                      {formatCurrency(proc.taxAmount)}
                    </Text>
                  </Group>
                ) : null}
                {(proc.chargesAmount ?? 0) > 0 ? (
                  <Group justify="space-between">
                    <Text size="sm">
                      {chargesLabel} ({chargesDir === 'subtract' ? '−' : '+'})
                    </Text>
                    <Text
                      size="sm"
                      fw={600}
                      c={chargesDir === 'subtract' ? 'red' : undefined}
                    >
                      {chargesDir === 'subtract' ? '− ' : '+ '}
                      {formatCurrency(Math.abs(Number(proc.chargesAmount)))}
                    </Text>
                  </Group>
                ) : null}
              </>
            );
          })()}
          <Divider />
          <Group justify="space-between">
            <Text fw={700}>{t('orders.total')}</Text>
            <Text fw={700}>{formatCurrency(proc.procAmount) ?? empty}</Text>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
